// @ts-nocheck
// Fluxo de criação de Clube/Grupo (5 passos) + Empty State pós-criação.
import React, { useState } from 'react';
import { CoverCropper } from './cover-cropper';
import { Icon } from '../icons';
import { useTerm } from './components';

export interface ClubFormData {
  name: string;
  coverTemplate: 'embers' | 'sage' | 'plum' | 'custom';
  customCover?: string | null;
  customBanner?: string | null;
  description: string;
  mode: 'group-read' | 'shared-habit';
  privacy: 'open' | 'closed';
  postBy: 'leader-only' | 'moderators' | 'all' | 'approval';
  storiesPostBy?: 'leader-only' | 'moderators' | 'all';
  openWhen: 'always' | 'end' | 'days' | 'milestones';
  openDays: number[];
  allowPoke: boolean;
  publicRanking: boolean;
  allowMemberQuiz: boolean;
  // Livro do clube (só usado em group-read, single-book legado)
  book?: {
    title: string;
    author: string;
    totalPages: number;
    coverHue?: number;
    coverStyle?: string;
  };
  // Cronograma multi-livro (group-read). Quando totalBooksPlanned > 1, o
  // step 'book' singular vira 'sequence' (lista de slots).
  totalBooksPlanned?: number;
  durationCount?: number;
  durationUnit?: 'semanas' | 'meses' | 'ano';
  noDeadline?: boolean;
  // quantos amigos o líder pretende chamar — só informativo (ambienta o convite)
  friendsTarget?: number;
  // Clube Social: campos da PROPOSTA (co-criação) — só quando propose=true
  propose?: boolean;
  objective?: string;
  rulesText?: string;
  proposedStartAt?: string | null;
  sequenceBooks?: Array<null | {
    title: string;
    author: string;
    totalPages: number;
    coverHue?: number;
    coverStyle?: string;
    coverUrl?: string | null;
    bookId?: string | null;
  }>;
  startEmpty?: boolean;
}

const COVER_TEMPLATES = [
  { id: 'embers', name: 'Brasas', bg: 'linear-gradient(140deg, oklch(0.42 0.12 35) 0%, oklch(0.28 0.08 25) 100%)', accent: '#E87A3B', motif: 'flame' },
  { id: 'sage',   name: 'Sálvia', bg: 'linear-gradient(150deg, oklch(0.58 0.06 140) 0%, oklch(0.34 0.04 145) 100%)', accent: '#CFD5C1', motif: 'leaf' },
  { id: 'plum',   name: 'Ameixa', bg: 'linear-gradient(160deg, oklch(0.42 0.10 340) 0%, oklch(0.28 0.06 320) 100%)', accent: '#D9B0C8', motif: 'spark' },
];

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', borderRadius: 14,
  border: '1px solid var(--line)',
  background: 'var(--paper-2)',
  fontSize: 15, fontFamily: 'inherit',
  color: 'var(--ink)', outline: 'none',
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mono" style={{
    marginTop: 18, marginBottom: 6,
    fontSize: 9.5, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'var(--ink-3)',
    fontWeight: 600,
  }}>{children}</div>
);

const CoverPreview: React.FC<{ template: any; name?: string; small?: boolean; customUrl?: string | null }> = ({ template, name, small = false, customUrl }) => {
  if (!template && !customUrl) return null;
  const h = small ? 88 : 140;
  return (
    <div style={{
      width: '100%', height: h, borderRadius: 16,
      background: customUrl ? '#161210' : (template ? template.bg : 'var(--paper-2)'),
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'flex-end',
      padding: small ? 10 : 16,
      color: '#F3ECDE',
    }}>
      {customUrl && <img src={customUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      {customUrl && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.55), transparent 50%)' }} />}
      {!customUrl && template && <div style={{ position: 'absolute', top: small ? -12 : -20, right: small ? -16 : -24, opacity: 0.35 }}>
        {template.motif === 'flame' && (
          <svg width={small ? 90 : 140} height={small ? 90 : 140} viewBox="0 0 40 60" fill={template.accent}>
            <path d="M20 56C10 50 6 38 12 26c1-2 4-2 5 0 2 4 2 8 5 10 1-6-3-12 0-18 0-2 3-3 5-1 6 6 10 14 10 22 0 12-9 22-17 22-2 0-2-3 0-5z"/>
          </svg>
        )}
        {template.motif === 'leaf' && (
          <svg width={small ? 90 : 140} height={small ? 90 : 140} viewBox="0 0 60 60" fill={template.accent}>
            <path d="M50 8C24 12 8 28 6 50c0 4 4 6 7 4 14-12 30-22 38-32 2-4-1-15-1-14z"/>
            <path d="M14 46l32-30" stroke={template.accent} strokeWidth="1.5" fill="none" opacity="0.5"/>
          </svg>
        )}
        {template.motif === 'spark' && (
          <svg width={small ? 90 : 140} height={small ? 90 : 140} viewBox="0 0 60 60" fill={template.accent}>
            <path d="M30 4l4 18 18 4-18 4-4 18-4-18-18-4 18-4z"/>
          </svg>
        )}
      </div>}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="mono" style={{
          fontSize: small ? 8 : 9.5, letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(243,236,222,0.7)',
        }}>capa do clube</div>
        <div className="serif" style={{
          fontSize: small ? 18 : 28, lineHeight: 1.05,
          marginTop: 4, color: '#F3ECDE',
        }}>{name || 'Sem nome ainda'}</div>
      </div>
    </div>
  );
};

const StepperBar: React.FC<{ step: number; total?: number; labels: string[] }> = ({ step, total = 5, labels }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const isCurrent = idx === step;
        const isPast = idx < step;
        return (
          <React.Fragment key={i}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              background: isCurrent ? 'var(--ember)' : isPast ? 'var(--ink)' : 'var(--paper-3)',
              color: isCurrent ? 'var(--paper)' : isPast ? 'var(--paper)' : 'var(--ink-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600, fontFamily: 'Geist Mono, monospace',
              flexShrink: 0,
              boxShadow: isCurrent ? '0 6px 14px -4px rgba(199,83,26,0.5)' : 'none',
              transition: 'all 200ms',
            }}>
              {isPast ? <Icon name="check" size={11} color="var(--paper)" stroke={3}/> : idx}
            </div>
            {i < total - 1 && (
              <div style={{
                flex: 1, height: 2, borderRadius: 1,
                background: idx < step ? 'var(--ink)' : 'var(--paper-3)',
                transition: 'background 200ms',
              }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
    <div className="mono" style={{
      fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
      color: 'var(--ink-3)', marginTop: 12,
    }}>
      Passo {step} de {total} · <span style={{ color: 'var(--ember)' }}>{labels[step - 1]}</span>
    </div>
  </div>
);

const Step1Identity: React.FC<{
  data: ClubFormData;
  setData: (d: ClubFormData) => void;
  T: any;
  onUploadCover?: (file: File) => Promise<string>;
}> = ({ data, setData, T, onUploadCover }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [coverPicked, setCoverPicked] = useState<any>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerPicked, setBannerPicked] = useState<any>(null);

  const handleFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (!onUploadCover) {
      setUploadError('Upload não disponível.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadError('Imagem muito grande (máx 8 MB).');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const url = await onUploadCover(file);
      // Marca coverTemplate como 'custom' pra o backend usar customCoverUrl
      setData({ ...data, coverTemplate: 'custom' as any, customCover: url });
    } catch (e: any) {
      setUploadError(e?.message || 'Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleBannerFile = async (file: File | null | undefined) => {
    if (!file || !onUploadCover) return;
    if (file.size > 8 * 1024 * 1024) { setUploadError('Imagem muito grande (máx 8 MB).'); return; }
    setUploadingBanner(true); setUploadError(null);
    try {
      const url = await onUploadCover(file);
      setData({ ...data, customBanner: url });
    } catch (e: any) {
      setUploadError(e?.message || 'Erro ao enviar banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const descLeft = 200 - (data.description || '').length;
  const tplObj = COVER_TEMPLATES.find(t => t.id === data.coverTemplate);
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Como vai se chamar<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>esse {T.s}</span>?
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Nome, capa e descrição. Tudo isso pode mudar depois.
      </div>

      <FieldLabel>Nome do {T.s}</FieldLabel>
      <input
        value={data.name || ''}
        onChange={e => setData({ ...data, name: e.target.value })}
        placeholder={T.s === 'clube' ? 'ex: Os Maratonistas' : 'ex: Lendo Bonhoeffer juntos'}
        style={inputStyle}
      />

      <FieldLabel>Capa</FieldLabel>
      <CoverPreview template={tplObj || COVER_TEMPLATES[0]} name={data.name} customUrl={data.customCover}/>
      <div className="mono" style={{
        fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--ink-3)', marginTop: 14, marginBottom: 8,
      }}>Escolher um template</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {COVER_TEMPLATES.map(t => {
          const sel = data.coverTemplate === t.id;
          return (
            <button key={t.id} onClick={() => setData({ ...data, coverTemplate: t.id as any, customCover: null })} aria-pressed={sel} style={{
              padding: 4, borderRadius: 12,
              border: '2px solid ' + (sel ? 'var(--ember)' : 'transparent'),
              background: 'transparent', transition: 'all 160ms',
            }}>
              <div style={{ height: 64, borderRadius: 8, background: t.bg, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -8, right: -8, opacity: 0.4 }}>
                  {t.motif === 'flame' && <svg width="38" height="38" viewBox="0 0 40 60" fill={t.accent}><path d="M20 56C10 50 6 38 12 26c2 4 2 8 5 10 1-6-3-12 0-18 6 6 12 14 12 22 0 12-9 22-17 22z"/></svg>}
                  {t.motif === 'leaf' && <svg width="38" height="38" viewBox="0 0 60 60" fill={t.accent}><path d="M50 8C24 12 8 28 6 50c14-12 30-22 38-32 2-4-1-15-1-14z"/></svg>}
                  {t.motif === 'spark' && <svg width="38" height="38" viewBox="0 0 60 60" fill={t.accent}><path d="M30 4l4 18 18 4-18 4-4 18-4-18-18-4 18-4z"/></svg>}
                </div>
              </div>
              <div className="mono" style={{
                marginTop: 6, fontSize: 9, letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: sel ? 'var(--ember)' : 'var(--ink-3)',
                fontWeight: sel ? 600 : 500, textAlign: 'center',
              }}>{t.name}</div>
            </button>
          );
        })}
      </div>
      {/* capa custom já aparece no preview acima — aqui só a opção de remover */}
      {data.customCover && (
        <button onClick={() => setData({ ...data, coverTemplate: 'embers' as any, customCover: null })} style={{
          marginTop: 8, width: '100%', padding: 10, background: 'transparent',
          color: 'var(--ink-3)', fontSize: 12.5, border: 'none', cursor: 'pointer',
        }}>Remover imagem · voltar ao template</button>
      )}
      <label style={{
        marginTop: 10, width: '100%', padding: '12px',
        borderRadius: 12, border: '1px dashed var(--line-strong)',
        background: 'transparent', color: 'var(--ink-2)',
        fontSize: 12.5, fontWeight: 500,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        cursor: uploading ? 'default' : 'pointer',
        opacity: uploading ? 0.6 : 1,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="3"/>
          <path d="M3 16l5-5 4 4 6-6 3 3"/>
          <circle cx="9" cy="9" r="1.5"/>
        </svg>
        {uploading ? 'Enviando…' : (data.customCover ? 'Trocar imagem' : 'Enviar minha imagem')}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          disabled={uploading}
          onChange={e => { const f = e.target.files?.[0]; e.currentTarget.value = ''; if (f) setCoverPicked(f); }}
          style={{ display: 'none' }}
        />
      </label>
      {uploadError && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: '#C03A2A' }}>{uploadError}</div>
      )}

      {/* Banner do clube (4:3) — opcional, aparece dentro do clube */}
      <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Banner do clube <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>· 4:3, aparece dentro do clube (opcional)</span></div>
      {data.customBanner && (
        <div style={{ position: 'relative', marginTop: 8, borderRadius: 12, overflow: 'hidden', aspectRatio: '4 / 3' }}>
          <img src={data.customBanner} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <button onClick={() => setData({ ...data, customBanner: null })} style={{ position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }} title="Remover banner">×</button>
        </div>
      )}
      <label style={{ marginTop: 8, width: '100%', padding: '12px', borderRadius: 12, border: '1px dashed var(--line-strong)', background: 'transparent', color: 'var(--ink-2)', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: uploadingBanner ? 'default' : 'pointer', opacity: uploadingBanner ? 0.6 : 1 }}>
        {uploadingBanner ? 'Enviando…' : (data.customBanner ? 'Trocar banner' : 'Adicionar banner')}
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploadingBanner} onChange={e => { const f = e.target.files?.[0]; e.currentTarget.value = ''; if (f) setBannerPicked(f); }} style={{ display: 'none' }} />
      </label>

      <FieldLabel>Descrição</FieldLabel>
      <textarea
        value={data.description || ''}
        onChange={e => setData({ ...data, description: e.target.value.slice(0, 200) })}
        placeholder="Em 2 frases: do que esse clube vive."
        style={{ ...inputStyle, minHeight: 72, resize: 'none', lineHeight: 1.45 }}
      />
      <div style={{
        display: 'flex', justifyContent: 'flex-end', marginTop: 4,
        fontFamily: 'Geist Mono, monospace',
        fontSize: 10, color: descLeft < 30 ? 'var(--ember)' : 'var(--ink-4)',
        letterSpacing: '0.06em',
      }}>
        {descLeft} caracteres restantes
      </div>
      {coverPicked && <CoverCropper file={coverPicked} onCancel={() => setCoverPicked(null)} onConfirm={(cf) => { setCoverPicked(null); handleFile(cf); }} />}
      {bannerPicked && <CoverCropper file={bannerPicked} aspect={4/3} outW={1440} outH={1080} title="Ajustar banner" hint="Arraste pra posicionar · proporção 4:3 (banner do clube)" onCancel={() => setBannerPicked(null)} onConfirm={(cf) => { setBannerPicked(null); handleBannerFile(cf); }} />}
    </div>
  );
};

const Step2Mode: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData, T }) => (
  <div style={{ padding: '0 20px' }}>
    <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
      Como vocês vão<br/>
      <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>ler juntos</span>?
    </div>
    <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
      Esta escolha define como o {T.s} se organiza por dentro.
    </div>
    <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <CreateModeCard
        active={data.mode === 'group-read'} onClick={() => setData({ ...data, mode: 'group-read' })}
        title="Leitura em grupo" italic="o mesmo livro"
        desc={`Todos leem o mesmo livro juntos. Ideal para ${T.p} de discussão e estudo em conjunto.`}
        icon={<svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect x="9" y="6" width="22" height="28" rx="2" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M14 12h12M14 17h12M14 22h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>}
      />
      <CreateModeCard
        active={data.mode === 'shared-habit'} onClick={() => setData({ ...data, mode: 'shared-habit' })}
        title="Leitura avulsa" italic="cada um o seu"
        desc={`Cada um lê o que quiser. Foco em criar hábito coletivo. Ideal para ${T.p} de motivação.`}
        icon={<svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect x="6" y="9" width="9" height="22" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
          <rect x="16" y="6" width="9" height="25" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
          <rect x="26" y="11" width="9" height="20" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
        </svg>}
      />
    </div>
  </div>
);

const CreateModeCard: React.FC<{ active: boolean; onClick: () => void; title: string; italic: string; desc: string; icon: React.ReactNode }> = ({ active, onClick, title, italic, desc, icon }) => (
  <button onClick={onClick} aria-pressed={active} style={{
    width: '100%', textAlign: 'left',
    padding: 18, borderRadius: 22,
    background: active ? 'var(--paper-2)' : 'var(--paper)',
    border: active ? '2px solid var(--ember)' : '1px solid var(--line)',
    transition: 'all 200ms',
    boxShadow: active ? '0 16px 32px -16px rgba(199,83,26,0.35)' : 'none',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: active ? 'var(--ember-soft)' : 'var(--paper-3)',
        color: active ? 'var(--ember)' : 'var(--ink-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="serif" style={{ fontSize: 20, lineHeight: 1.15 }}>{title}</div>
        <div className="serif" style={{
          fontSize: 14, lineHeight: 1.2, fontStyle: 'italic',
          color: 'var(--ink-3)', marginTop: 2,
        }}>· {italic}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.45 }}>{desc}</div>
      </div>
      <div style={{
        flexShrink: 0,
        width: 22, height: 22, borderRadius: '50%',
        background: active ? 'var(--ember)' : 'var(--paper-3)',
        border: active ? 'none' : '1px solid var(--line-strong)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {active && <Icon name="check" size={12} color="var(--paper)" stroke={3}/>}
      </div>
    </div>
  </button>
);

// Sub-step exclusivo de group-read: escolher o livro do clube.
const Step3Book: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData, T }) => {
  const book = data.book || { title: '', author: '', totalPages: 0 };
  const update = (patch: Partial<ClubFormData['book']>) => setData({ ...data, book: { ...book, ...patch } as any });
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Qual o<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>livro do {T.s}</span>?
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Todos vão ler esse livro juntos. Você pode trocar depois pelas configurações do {T.s}.
      </div>

      <FieldLabel>Título do livro</FieldLabel>
      <input
        value={book.title || ''}
        onChange={e => update({ title: e.target.value })}
        placeholder="Ex: Discipulado"
        style={inputStyle}
      />

      <FieldLabel>Autor</FieldLabel>
      <input
        value={book.author || ''}
        onChange={e => update({ author: e.target.value })}
        placeholder="Ex: Dietrich Bonhoeffer"
        style={inputStyle}
      />

      <FieldLabel>Total de páginas</FieldLabel>
      <input
        type="number" inputMode="numeric"
        value={book.totalPages || ''}
        onChange={e => update({ totalPages: parseInt(e.target.value, 10) || 0 })}
        placeholder="Ex: 312"
        min={1} max={500000}
        style={inputStyle}
      />

      <FieldLabel>Tom da capa (cor)</FieldLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
        {[10, 30, 60, 140, 220, 320].map(hue => {
          const sel = (book.coverHue ?? 22) === hue;
          return (
            <button key={hue} onClick={() => update({ coverHue: hue })} style={{
              height: 44, borderRadius: 10,
              background: `linear-gradient(135deg, oklch(0.78 0.09 ${hue}), oklch(0.45 0.10 ${hue}))`,
              border: sel ? '2px solid var(--ember)' : '1px solid var(--line)',
              boxShadow: sel ? '0 0 0 2px var(--paper-2), 0 6px 14px -4px rgba(0,0,0,0.2)' : 'none',
            }}/>
          );
        })}
      </div>

      <div className="mono" style={{
        marginTop: 18, fontSize: 9, letterSpacing: '0.16em',
        color: 'var(--ink-4)', textTransform: 'uppercase',
      }}>
        ✦ Essa escolha afeta os marcos de leitura e o estudo do livro.
      </div>
    </div>
  );
};

const Step3Privacy: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData }) => {
  const opts = [
    { id: 'open',   title: 'Público',  icon: '🌐', desc: 'Aparece no Explorar e qualquer pessoa pode encontrar e entrar.', who: 'Visível · entrada livre' },
    { id: 'closed', title: 'Fechado',  icon: '🔒', desc: 'Não aparece em buscas. Só entra quem recebe convite seu.',         who: 'Invisível · só por convite' },
  ];
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Quem pode<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>encontrar e entrar</span>?
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        A privacidade muda a porta de entrada. Isso pode ser ajustado depois.
      </div>
      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {opts.map(o => {
          const sel = data.privacy === o.id;
          return (
            <button key={o.id} aria-pressed={sel} onClick={() => setData({ ...data, privacy: o.id as any })} style={{
              width: '100%', textAlign: 'left',
              padding: 16, borderRadius: 18,
              background: sel ? 'var(--paper-2)' : 'var(--paper)',
              border: '2px solid ' + (sel ? 'var(--ember)' : 'transparent'),
              boxShadow: sel ? '0 12px 24px -12px rgba(199,83,26,0.30)' : 'inset 0 0 0 1px var(--line)',
              display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: sel ? 'var(--ember-soft)' : 'var(--paper-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, flexShrink: 0,
              }}>{o.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}>{o.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>{o.desc}</div>
                <div className="mono" style={{
                  marginTop: 8, fontSize: 9.5, letterSpacing: '0.12em',
                  color: sel ? 'var(--ember)' : 'var(--ink-4)',
                  textTransform: 'uppercase',
                }}>{o.who}</div>
              </div>
              <div style={{
                flexShrink: 0,
                width: 22, height: 22, borderRadius: '50%',
                background: sel ? 'var(--ember)' : 'transparent',
                border: '2px solid ' + (sel ? 'var(--ember)' : 'var(--line-strong)'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {sel && <Icon name="check" size={11} color="var(--paper)" stroke={3}/>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const RuleSection: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div style={{
    marginTop: 22, padding: 14, borderRadius: 18,
    background: 'var(--paper-2)', border: '1px solid var(--line)',
  }}>
    <div className="mono" style={{
      fontSize: 9.5, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--ember)', fontWeight: 600,
    }}>{title}</div>
    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4, marginBottom: 10 }}>{sub}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
  </div>
);

const RadioRow: React.FC<{ value: string; current: string; label: string; sub?: string; onPick: (v: string) => void }> = ({ value, current, label, sub, onPick }) => {
  const sel = current === value;
  return (
    <button onClick={() => onPick(value)} aria-pressed={sel} style={{
      width: '100%', textAlign: 'left',
      padding: '10px 4px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      background: 'transparent', borderRadius: 8,
    }}>
      <div style={{
        flexShrink: 0,
        width: 18, height: 18, borderRadius: '50%',
        border: '2px solid ' + (sel ? 'var(--ember)' : 'var(--line-strong)'),
        background: sel ? 'var(--paper)' : 'transparent',
        position: 'relative', marginTop: 2,
      }}>
        {sel && (
          <div style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: 'var(--ember)' }}/>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
      </div>
    </button>
  );
};

const ToggleRow: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string }> = ({ checked, onChange, label, sub }) => (
  <div style={{ padding: '10px 4px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
    </div>
    <button onClick={() => onChange(!checked)} role="switch" aria-checked={checked} style={{
      flexShrink: 0,
      width: 42, height: 24, borderRadius: 999,
      background: checked ? 'var(--ember)' : 'var(--paper-3)',
      border: '1px solid ' + (checked ? 'var(--ember)' : 'var(--line-strong)'),
      position: 'relative', marginTop: 2, transition: 'all 160ms',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: checked ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%',
        background: 'var(--paper)', transition: 'left 160ms',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
      }}/>
    </button>
  </div>
);

const Step4Rules: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData, T }) => {
  const set = (key: string, val: any) => setData({ ...data, [key]: val });
  const days = ['S','T','Q','Q','S','S','D'];
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 28, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Como o {T.s}<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>vai funcionar</span>.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Pequenas regras que dão ritmo. Tudo pode ser ajustado depois.
      </div>

      <RuleSection title="Quem pode postar insights" sub="Define quem cria reflexões, citações e perguntas no Estudo do livro.">
        <RadioRow value="leader-only" current={data.postBy} label="Apenas o líder" sub="Mural curado." onPick={v => set('postBy', v)}/>
        <RadioRow value="moderators" current={data.postBy} label="Líder + moderadores" sub="Líder e moderadores escolhidos por ele." onPick={v => set('postBy', v)}/>
        <RadioRow value="all" current={data.postBy} label="Todos os membros" sub="Conversa aberta." onPick={v => set('postBy', v)}/>
        <RadioRow value="approval" current={data.postBy} label="Membros, com aprovação do líder" sub="Equilíbrio entre os dois." onPick={v => set('postBy', v)}/>
      </RuleSection>

      <RuleSection title="Quem pode postar story" sub="Stories são fotos/textos rápidos que aparecem 24h no topo do Estudo.">
        <RadioRow value="leader-only" current={data.storiesPostBy || 'all'} label="Apenas o líder" sub="Conteúdo direto da fonte." onPick={v => set('storiesPostBy', v)}/>
        <RadioRow value="moderators" current={data.storiesPostBy || 'all'} label="Líder + moderadores" sub="" onPick={v => set('storiesPostBy', v)}/>
        <RadioRow value="all" current={data.storiesPostBy || 'all'} label="Todos os membros" sub="Todo mundo compartilha a leitura." onPick={v => set('storiesPostBy', v)}/>
      </RuleSection>

      <RuleSection title="Quando o mural fica aberto" sub="Quando os membros podem ver e postar insights.">
        <RadioRow value="always" current={data.openWhen} label="Sempre aberto" sub="Padrão. O mural está sempre lá." onPick={v => set('openWhen', v)}/>
        <RadioRow value="end" current={data.openWhen} label="Só no final do livro" sub="Bloqueia spoilers até a leitura terminar." onPick={v => set('openWhen', v)}/>
        <RadioRow value="days" current={data.openWhen} label="Em dias específicos" sub="Foco semanal." onPick={v => set('openWhen', v)}/>
        {data.openWhen === 'days' && (
          <div style={{
            marginTop: 8, padding: '10px',
            background: 'var(--paper)', borderRadius: 12,
            display: 'flex', gap: 6, justifyContent: 'space-between',
          }}>
            {days.map((d, i) => {
              const sel = (data.openDays || []).includes(i);
              return (
                <button key={i} aria-pressed={sel} onClick={() => {
                  const cur = data.openDays || [];
                  set('openDays', sel ? cur.filter(x => x !== i) : [...cur, i]);
                }} style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: sel ? 'var(--ember)' : 'var(--paper-2)',
                  color: sel ? 'var(--paper)' : 'var(--ink-3)',
                  fontSize: 11, fontWeight: 600,
                  border: '1px solid ' + (sel ? 'var(--ember)' : 'var(--line)'),
                  transition: 'all 160ms',
                }}>{d}</button>
              );
            })}
          </div>
        )}
        <RadioRow value="milestones" current={data.openWhen} label="Por marco" sub="Destrava conforme o grupo avança no livro." onPick={v => set('openWhen', v)}/>
      </RuleSection>

      <RuleSection title="Configurações sociais" sub="Como os membros se relacionam entre si.">
        <ToggleRow checked={data.allowPoke ?? true} onChange={v => set('allowPoke', v)} label="Permitir 'cutucar' inativos" sub="Membros podem mandar lembretes amigáveis a quem ficou pra trás."/>
        <ToggleRow checked={data.publicRanking ?? true} onChange={v => set('publicRanking', v)} label="Mostrar ranking publicamente" sub="Quando desligado, só o líder vê o ranking."/>
        {data.mode === 'group-read' && (
          <ToggleRow checked={data.allowMemberQuiz ?? false} onChange={v => set('allowMemberQuiz', v)} label="Membros podem criar quiz/enquetes" sub="Por padrão, só o líder. Mantenha desligado se preferir mural curado."/>
        )}
      </RuleSection>
    </div>
  );
};

const SummaryRow: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div style={{
    padding: '12px 14px', borderRadius: 14,
    background: 'var(--paper-2)', border: '1px solid var(--line)',
    display: 'flex', alignItems: 'center', gap: 12,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10,
      background: 'var(--paper)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 17, flexShrink: 0,
    }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="mono" style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--ink-3)', fontWeight: 500,
      }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--ink)', marginTop: 2, lineHeight: 1.3 }}>{value}</div>
    </div>
  </div>
);

// ─── StepSchedule (group-read) — quantos livros + em quanto tempo ───
const StepSchedule: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData }) => {
  const presets = [1, 4, 12];
  const sel = data.totalBooksPlanned;
  const isOther = sel != null && !presets.includes(sel);
  const [otherOpen, setOtherOpen] = useState(isOther);

  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Quantos livros<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{data.mode === 'shared-habit' ? 'voce vai ler' : 'voces vao ler'}</span>?
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        {data.mode === 'shared-habit'
          ? 'Sua jornada pessoal. Os outros membros montam a deles separadamente.'
          : 'Pode escolher so um, ou uma jornada inteira.'}
      </div>

      <FieldLabel>Total de livros</FieldLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {presets.map(n => {
          const isSel = !otherOpen && sel === n;
          return (
            <button
              key={n}
              onClick={() => { setData({ ...data, totalBooksPlanned: n }); setOtherOpen(false); }}
              style={{
                padding: '14px 6px',
                borderRadius: 14,
                background: isSel ? 'var(--ember)' : 'var(--paper-2)',
                color: isSel ? 'var(--paper)' : 'var(--ink)',
                border: '1px solid ' + (isSel ? 'var(--ember)' : 'var(--line)'),
                fontFamily: 'Instrument Serif, serif',
                fontSize: 26, lineHeight: 1,
                boxShadow: isSel ? '0 12px 24px -10px rgba(199,83,26,0.5)' : 'none',
                transition: 'all 160ms',
              }}
            >
              {n}
              <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 6, opacity: 0.75 }}>
                {n === 1 ? 'um' : n === 4 ? 'trimestre' : 'ano'}
              </div>
            </button>
          );
        })}
        <button
          onClick={() => { setOtherOpen(true); if (presets.includes(sel as number)) setData({ ...data, totalBooksPlanned: null as any }); }}
          style={{
            padding: '14px 6px',
            borderRadius: 14,
            background: otherOpen ? 'var(--ember)' : 'var(--paper-2)',
            color: otherOpen ? 'var(--paper)' : 'var(--ink-2)',
            border: '1px solid ' + (otherOpen ? 'var(--ember)' : 'var(--line)'),
            fontFamily: 'Instrument Serif, serif',
            fontSize: 18, lineHeight: 1, fontStyle: 'italic',
            transition: 'all 160ms',
          }}
        >
          Outro
          <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 6, opacity: 0.75, fontStyle: 'normal' }}>
            custom
          </div>
        </button>
      </div>
      {otherOpen && (
        <div style={{
          marginTop: 10, padding: 12,
          borderRadius: 14, background: 'var(--paper-2)',
          border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <input
            type="number" min={1} max={120}
            value={isOther && sel != null ? sel : ''}
            onChange={e => setData({ ...data, totalBooksPlanned: Math.max(1, parseInt(e.target.value || '1', 10)) })}
            placeholder="ex: 6"
            style={{ ...inputStyle, padding: '10px 14px', fontSize: 18, flex: 1 }}
          />
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>livros</span>
        </div>
      )}

      <FieldLabel>Em quanto tempo?</FieldLabel>
      <div style={{
        padding: 12,
        borderRadius: 14, background: 'var(--paper-2)',
        border: '1px solid var(--line)',
        opacity: data.noDeadline ? 0.5 : 1,
        transition: 'opacity 200ms',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="number" min={1} max={365}
            disabled={!!data.noDeadline}
            value={data.durationCount || ''}
            onChange={e => setData({ ...data, durationCount: Math.max(1, parseInt(e.target.value || '1', 10)), prazoTocado: true } as any)}
            placeholder="4"
            style={{ ...inputStyle, padding: '10px 12px', fontSize: 18, width: 80 }}
          />
          <select
            disabled={!!data.noDeadline}
            value={data.durationUnit || 'meses'}
            onChange={e => setData({ ...data, durationUnit: e.target.value as any, prazoTocado: true } as any)}
            style={{
              flex: 1, padding: '12px 12px', borderRadius: 12,
              border: '1px solid var(--line)',
              background: 'var(--paper)', fontSize: 14,
              color: 'var(--ink)', fontFamily: 'inherit', outline: 'none',
            }}
          >
            <option value="semanas">semanas</option>
            <option value="meses">meses</option>
            <option value="ano">ano</option>
          </select>
        </div>
      </div>
      <label style={{
        marginTop: 10, padding: 12,
        background: 'var(--paper-2)', borderRadius: 14,
        border: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', gap: 10,
        cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={!!data.noDeadline}
          onChange={e => setData({ ...data, noDeadline: e.target.checked })}
          style={{ width: 18, height: 18, accentColor: 'var(--ember)' }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14 }}>Sem prazo definido</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>Sequência aberta — vocês decidem o ritmo.</div>
        </div>
      </label>
    </div>
  );
};

// ─── StepSequence (group-read) — slots de livros da jornada ───
const StepSequence: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any; library?: any[] }> = ({ data, setData, library = [] }) => {
  const total = data.totalBooksPlanned || 1;
  const slots = data.sequenceBooks || [];
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<any>({ title: '', author: '', totalPages: 0, coverHue: 22, coverStyle: 'paper' });
  const [pickMode, setPickMode] = useState(true);   // mostra "Meus livros" primeiro
  const [q, setQ] = useState('');
  const startEmptyOk = !!data.startEmpty;

  // livros da biblioteca ainda não usados em outro slot + filtro de busca
  const usedBookIds = new Set((slots || []).filter(Boolean).map((b: any) => b && b.bookId).filter(Boolean));
  const libFiltered = (library || []).filter((b: any) => {
    if (usedBookIds.has(b.id)) return false;
    const t = (q || '').trim().toLowerCase();
    return !t || ((b.title || '') + ' ' + (b.author || '')).toLowerCase().includes(t);
  });

  const openEdit = (idx: number) => {
    const existing = slots[idx];
    setDraft(existing || { title: '', author: '', totalPages: 0, coverHue: 22, coverStyle: 'paper' });
    setQ('');
    setPickMode(!existing && (library || []).length > 0);  // editando um já preenchido → abre no manual
    setEditingIdx(idx);
  };
  const pickFromLibrary = (b: any) => {
    if (editingIdx == null) return;
    const next = slots.slice();
    next[editingIdx] = { title: b.title, author: b.author, totalPages: b.totalPages || b.pages || 0, coverHue: b.coverHue ?? 22, coverUrl: b.coverUrl || null, bookId: b.id || null };
    setData({ ...data, sequenceBooks: next });
    setEditingIdx(null);
  };
  const saveDraft = () => {
    if (editingIdx == null) return;
    if (!draft.title?.trim() || !draft.author?.trim() || !draft.totalPages) return;
    const next = slots.slice();
    next[editingIdx] = draft;
    setData({ ...data, sequenceBooks: next });
    setEditingIdx(null);
  };
  const clearSlot = (idx: number) => {
    const next = slots.slice();
    next[idx] = null;
    setData({ ...data, sequenceBooks: next });
  };

  const arr = Array.from({ length: total });

  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 28, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Defina os livros<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{data.mode === 'shared-habit' ? 'da sua fila' : 'da jornada'}</span>.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        {data.mode === 'shared-habit'
          ? 'Estes ficam na SUA fila pessoal. Cada membro adiciona os deles depois.'
          : 'Pode escolher tudo agora ou definir os próximos depois.'}
      </div>

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {arr.map((_, idx) => {
          const book = slots[idx];
          const filled = !!book;
          return (
            <div
              key={idx}
              style={{
                padding: 12, borderRadius: 16,
                background: filled ? 'var(--paper-2)' : 'transparent',
                border: filled ? '1px solid var(--line)' : '1.5px dashed var(--line-strong)',
                display: 'flex', alignItems: 'center', gap: 12,
                opacity: startEmptyOk ? 0.45 : 1,
                transition: 'all 200ms',
              }}
            >
              <div style={{
                flexShrink: 0,
                width: 44, height: 56, borderRadius: 10,
                background: filled ? 'var(--ember-soft)' : 'var(--paper-2)',
                color: filled ? 'var(--ember)' : 'var(--ink-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Instrument Serif, serif', fontSize: 22, fontStyle: 'italic',
              }}>
                #{idx + 1}
              </div>
              {filled ? (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{ fontSize: 16, lineHeight: 1.15 }}>{(book as any).title}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{(book as any).author} · {(book as any).totalPages} pág</div>
                  </div>
                  <button onClick={() => clearSlot(idx)} style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'var(--paper)', border: '1px solid var(--line)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--ink-3)', flexShrink: 0,
                  }}>
                    <Icon name="close" size={14}/>
                  </button>
                </>
              ) : (
                <button onClick={() => !startEmptyOk && openEdit(idx)} disabled={startEmptyOk}
                  style={{
                    flex: 1, padding: '12px 6px', background: 'transparent',
                    textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                    color: 'var(--ink-3)',
                  }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: '1.5px dashed var(--line-strong)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="serif" style={{ fontSize: 15, fontStyle: 'italic' }}>Toque pra escolher</div>
                  </div>
                </button>
              )}
              {/* "Definir depois" PULA de verdade (pente fino, UX-30). Antes era
                  só um subtítulo dentro do botão de escolher: tocar nele abria o
                  seletor de livro. É o mesmo "começar sem livro" da caixa lá
                  embaixo, que o servidor já aceita. */}
              {!filled && !startEmptyOk && idx === 0 && (
                <button onClick={() => setData({ ...data, startEmpty: true })} style={{
                  flexShrink: 0, padding: '8px 10px', borderRadius: 10,
                  background: 'transparent', border: '1px solid var(--line)',
                  fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--ink-3)', cursor: 'pointer',
                }}>Definir depois</button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: 18, padding: '12px 14px', borderRadius: 14,
        background: 'var(--paper-2)', border: '1px dashed var(--line-strong)',
        fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.55,
      }}>
        Você pode adicionar ou trocar livros a qualquer momento como líder.
      </div>

      <label style={{
        marginTop: 14, padding: 12,
        background: 'var(--paper-2)', borderRadius: 14,
        border: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', gap: 10,
        cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={startEmptyOk}
          onChange={e => setData({ ...data, startEmpty: e.target.checked })}
          style={{ width: 18, height: 18, accentColor: 'var(--ember)' }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14 }}>Começar sem definir nenhum livro ainda</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>Líderes podem definir os livros depois que os membros chegarem.</div>
        </div>
      </label>

      {/* Inline editor — sheet simples sobreposto pra preencher um slot */}
      {editingIdx != null && (
        <div onClick={() => setEditingIdx(null)} style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(10,8,6,0.55)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'flex-end',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', background: 'var(--paper)',
            borderRadius: '24px 24px 0 0', padding: 20,
            boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '0 0 12px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--line-strong)' }}/>
            </div>
            <div className="serif" style={{ fontSize: 22, lineHeight: 1.1 }}>
              {pickMode && library.length > 0 ? 'Escolher' : 'Adicionar'} <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>livro #{editingIdx + 1}</span>
            </div>

            {pickMode && library.length > 0 ? (
              <>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar nos meus livros…" style={{ ...inputStyle, marginTop: 14 }}/>
                <div style={{ marginTop: 10, maxHeight: '42vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {libFiltered.length === 0 && (
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: '20px 0' }}>
                      {q.trim() ? 'Nenhum livro seu combina com a busca.' : 'Você ainda não tem livros na sua biblioteca.'}
                    </div>
                  )}
                  {libFiltered.map((b: any) => (
                    <button key={b.id} onClick={() => pickFromLibrary(b)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderRadius: 12, background: 'var(--paper-2)', border: '1px solid var(--line)', textAlign: 'left', cursor: 'pointer' }}>
                      <div style={{ flexShrink: 0, width: 34, height: 46, borderRadius: 7, overflow: 'hidden', background: `linear-gradient(135deg, oklch(0.78 0.09 ${b.coverHue ?? 22}), oklch(0.45 0.10 ${b.coverHue ?? 22}))` }}>
                        {b.coverUrl && <img src={b.coverUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="serif" style={{ fontSize: 15, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.author}{b.totalPages ? ` · ${b.totalPages} pág` : ''}</div>
                      </div>
                      <Icon name="chevron-right" size={16} color="var(--ink-4)"/>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setDraft({ title: '', author: '', totalPages: 0, coverHue: 22, coverStyle: 'paper' }); setPickMode(false); }} style={{
                  marginTop: 12, width: '100%', padding: 13, borderRadius: 14,
                  background: 'transparent', border: '1px dashed var(--line-strong)', color: 'var(--ink-2)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                }}>+ Adicionar um livro que não está aqui</button>
                <button onClick={() => setEditingIdx(null)} style={{ marginTop: 8, width: '100%', padding: 12, background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>Cancelar</button>
              </>
            ) : (
              <>
                <FieldLabel>Título</FieldLabel>
                <input value={draft.title || ''} onChange={e => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Ex: Discipulado" style={inputStyle}/>
                <FieldLabel>Autor</FieldLabel>
                <input value={draft.author || ''} onChange={e => setDraft({ ...draft, author: e.target.value })}
                  placeholder="Ex: Dietrich Bonhoeffer" style={inputStyle}/>
                <FieldLabel>Total de páginas</FieldLabel>
                <input type="number" inputMode="numeric" min={1} max={500000}
                  value={draft.totalPages || ''}
                  onChange={e => setDraft({ ...draft, totalPages: parseInt(e.target.value, 10) || 0 })}
                  placeholder="Ex: 312" style={inputStyle}/>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => library.length > 0 ? setPickMode(true) : setEditingIdx(null)} style={{
                    flex: 1, padding: 14, borderRadius: 14,
                    background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer',
                  }}>{library.length > 0 ? '← Meus livros' : 'Cancelar'}</button>
                  <button onClick={saveDraft}
                    disabled={!draft.title?.trim() || !draft.author?.trim() || !draft.totalPages}
                    style={{
                      flex: 1, padding: 14, borderRadius: 14,
                      background: 'var(--ember)', color: 'var(--paper)',
                      fontSize: 14, fontWeight: 500, cursor: 'pointer',
                      opacity: (!draft.title?.trim() || !draft.author?.trim() || !draft.totalPages) ? 0.4 : 1,
                    }}>Salvar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── QUIZ inicial: perguntas humanas que derivam todos os defaults ───
   Substitui os passos Modo/Cronograma/Privacidade/Regras. Tudo editável depois. */
const qStepBtn: React.CSSProperties = { width: 46, height: 46, borderRadius: '50%', border: '1px solid var(--line-strong)', background: 'var(--paper-2)', color: 'var(--ink)', fontSize: 26, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 };
const QuizCard2: React.FC<{ selected: boolean; onClick: () => void; emoji?: string; title: string; desc?: string }> = ({ selected, onClick, emoji, title, desc }) => (
  <button onClick={onClick} style={{
    flex: 1, padding: '13px 12px', borderRadius: 14, textAlign: 'left',
    background: selected ? 'var(--ember-soft)' : 'var(--paper-2)',
    border: '2px solid ' + (selected ? 'var(--ember)' : 'var(--line)'),
    cursor: 'pointer', transition: 'all 160ms',
  }}>
    {emoji && <div style={{ fontSize: 20, marginBottom: 5 }}>{emoji}</div>}
    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2 }}>{title}</div>
    {desc && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.35 }}>{desc}</div>}
  </button>
);
const QuizStepper: React.FC<{ value: number; min?: number; max?: number; onChange: (v: number) => void }> = ({ value, min = 1, max = 99, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
    <button onClick={() => onChange(Math.max(min, value - 1))} style={qStepBtn}>−</button>
    <div className="serif" style={{ fontSize: 32, minWidth: 48, textAlign: 'center', color: 'var(--ink)' }}>{value}</div>
    <button onClick={() => onChange(Math.min(max, value + 1))} style={qStepBtn}>+</button>
  </div>
);
const QuizBlock: React.FC<{ q: string; children: React.ReactNode }> = ({ q, children }) => (
  <div style={{ marginTop: 22 }}>
    <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 10 }}>{q}</div>
    {children}
  </div>
);
const StepQuiz: React.FC<{ data: ClubFormData; setData: (d: ClubFormData) => void; T: any }> = ({ data, setData, T }) => {
  const set = (patch: Partial<ClubFormData>) => setData({ ...data, ...patch });
  const books = data.totalBooksPlanned || 1;
  const friends = (data as any).friendsTarget || 3;
  const hasDeadline = !data.noDeadline;
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Vamos montar<br/>
        <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>seu {T.s}</span>.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Responde rapidinho — <strong style={{ color: 'var(--ink-2)' }}>tudo pode mudar depois</strong> nas configurações.
      </div>

      <QuizBlock q="Como vocês vão ler?">
        <div style={{ display: 'flex', gap: 8 }}>
          <QuizCard2 selected={data.mode === 'group-read'} onClick={() => set({ mode: 'group-read' })} emoji="📖" title="O mesmo livro, juntos" desc="Todos leem e discutem o mesmo livro" />
          <QuizCard2 selected={data.mode === 'shared-habit'} onClick={() => set({ mode: 'shared-habit' })} emoji="👥" title="Cada um lê o seu" desc="Hábito coletivo, leituras livres" />
        </div>
      </QuizBlock>

      <QuizBlock q="Quem pode entrar?">
        <div style={{ display: 'flex', gap: 8 }}>
          <QuizCard2 selected={data.privacy === 'closed'} onClick={() => set({ privacy: 'closed' })} emoji="🔒" title="Só quem eu convidar" desc="Privado, não aparece em buscas" />
          <QuizCard2 selected={data.privacy === 'open'} onClick={() => set({ privacy: 'open' })} emoji="🌐" title="Qualquer um" desc="Público, aparece no Explorar" />
        </div>
      </QuizBlock>

      <QuizBlock q={data.mode === 'shared-habit' ? 'Quantos livros na sua fila?' : 'Quantos livros vão ler?'}>
        <QuizStepper value={books} min={1} max={60} onChange={(v) => set({ totalBooksPlanned: v })} />
      </QuizBlock>

      <QuizBlock q="Tem prazo?">
        <div style={{ display: 'flex', gap: 8, marginBottom: hasDeadline ? 12 : 0 }}>
          <QuizCard2 selected={hasDeadline} onClick={() => set({ noDeadline: false, prazoTocado: true } as any)} title="Sim, temos um prazo" />
          <QuizCard2 selected={!hasDeadline} onClick={() => set({ noDeadline: true, prazoTocado: true } as any)} title="No nosso tempo" desc="Sem prazo definido" />
        </div>
        {hasDeadline && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="number" inputMode="numeric" min={1} max={365} value={data.durationCount || ''} onChange={e => set({ durationCount: parseInt(e.target.value, 10) || 0, prazoTocado: true } as any)} placeholder="3" style={{ ...inputStyle, width: 92, textAlign: 'center' }} />
            <select value={data.durationUnit || 'meses'} onChange={e => set({ durationUnit: e.target.value as any, prazoTocado: true } as any)} style={{ ...inputStyle, flex: 1 }}>
              <option value="semanas">semanas</option>
              <option value="meses">meses</option>
              <option value="ano">ano(s)</option>
            </select>
          </div>
        )}
      </QuizBlock>

      <QuizBlock q="Quem posta e discute?">
        <div style={{ display: 'flex', gap: 8 }}>
          <QuizCard2 selected={data.postBy === 'all'} onClick={() => set({ postBy: 'all', storiesPostBy: 'all' })} emoji="💬" title="Todos participam" desc="Conversa aberta entre membros" />
          <QuizCard2 selected={data.postBy === 'leader-only'} onClick={() => set({ postBy: 'leader-only', storiesPostBy: 'leader-only' })} emoji="📣" title="Só eu (líder)" desc="Mural curado, membros leem" />
        </div>
      </QuizBlock>

      <QuizBlock q="Quantos amigos você vai chamar?">
        <QuizStepper value={friends} min={1} max={500} onChange={(v) => set({ friendsTarget: v } as any)} />
        <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 8 }}>Só pra te lembrar de convidar — dá pra chamar mais a qualquer momento.</div>
      </QuizBlock>
    </div>
  );
};

/** "1 mês", "2 meses", "1 semana", "1 ano" (pente fino, UX-31). */
export function fmtPrazo(n: number, unidade: string): string {
  const u = unidade === 'semanas' ? (n === 1 ? 'semana' : 'semanas')
    : unidade === 'ano' ? (n === 1 ? 'ano' : 'anos')
    : (n === 1 ? 'mês' : 'meses');
  return `${n} ${u}`;
}

const Step5Confirm: React.FC<{ data: ClubFormData; T: any }> = ({ data }) => {
  const tplObj = COVER_TEMPLATES.find(t => t.id === data.coverTemplate) || COVER_TEMPLATES[0];
  const privLabel: any = { open: 'Público', closed: 'Fechado' };
  const privIcon: any = { open: '🌐', closed: '🔒' };
  const modeLabel = data.mode === 'group-read' ? 'Leitura em grupo · mesmo livro' : data.mode === 'shared-habit' ? 'Leitura avulsa · cada um o seu' : '—';
  const postByLabel: any = { 'leader-only': 'Apenas o líder', 'moderators': 'Líder + moderadores', 'all': 'Todos os membros', 'approval': 'Membros, com aprovação' };
  const seqBooks = (data.sequenceBooks || []).filter(Boolean) as any[];
  const booksSummary = data.startEmpty
    ? 'Definir depois'
    : (seqBooks.length > 0
        ? `${seqBooks.map((b) => b.title).slice(0, 3).join(', ')}${seqBooks.length > 3 ? ` +${seqBooks.length - 3}` : ''}`
        : `${data.totalBooksPlanned || 1} livro(s) · a definir`);
  // "1 meses" → "1 mês" (pente fino, UX-31). E o prazo que ninguém escolheu
  // (o padrão de 1 mês) diz que é padrão, em vez de parecer uma escolha.
  const scheduleSummary = data.noDeadline
    ? 'No nosso tempo (sem prazo)'
    : (data.durationCount
        ? `${fmtPrazo(data.durationCount, data.durationUnit || 'meses')}${(data as any).prazoTocado ? '' : ' (padrão — dá para mudar)'}`
        : '—');
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
        Confira como ficou.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Pode voltar e ajustar qualquer parte antes de criar.
      </div>

      <div style={{ marginTop: 18 }}>
        <CoverPreview template={tplObj} name={data.name || 'Sem nome'} customUrl={data.customCover}/>
      </div>

      <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        {data.description || <em style={{ color: 'var(--ink-4)' }}>Sem descrição</em>}
      </div>

      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SummaryRow icon="📖" label="Modo de leitura" value={modeLabel}/>
        <SummaryRow icon={privIcon[data.privacy] || '·'} label="Privacidade" value={privLabel[data.privacy] || '—'}/>
        <SummaryRow icon="📚" label="Livros" value={booksSummary}/>
        <SummaryRow icon="🗓️" label="Prazo" value={scheduleSummary}/>
        <SummaryRow icon="✏️" label="Quem posta" value={postByLabel[data.postBy] || '—'}/>
      </div>
      <div className="mono" style={{ marginTop: 16, fontSize: 9, letterSpacing: '0.14em', color: 'var(--ink-4)', textTransform: 'uppercase' }}>
        ✦ Tudo isso pode ser ajustado depois nas configurações do clube.
      </div>
    </div>
  );
};

const CelebrationScreen: React.FC<{ data: ClubFormData; T: any }> = ({ data, T }) => (
  <div style={{
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px', background: 'var(--paper)',
    animation: 'fadeIn 320ms ease-out',
  }}>
    <div style={{
      width: 88, height: 88, borderRadius: '50%',
      background: 'var(--ember-soft)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'celebPop 600ms cubic-bezier(0.22, 1.4, 0.36, 1)',
    }}>
      <div style={{
        width: 60, height: 60, borderRadius: '50%',
        background: 'var(--ember)', color: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name="check" size={28} color="var(--paper)" stroke={3}/>
      </div>
    </div>
    <div className="serif" style={{
      fontSize: 36, lineHeight: 1.05, textAlign: 'center',
      marginTop: 28, letterSpacing: '-0.01em',
    }}>
      Seu {T.s} <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>existe</span>.
    </div>
    <div style={{
      fontSize: 13, color: 'var(--ink-3)', textAlign: 'center',
      marginTop: 10, lineHeight: 1.5, maxWidth: 280,
    }}>
      <strong style={{ color: 'var(--ink)' }}>{data.name || 'Sem nome'}</strong> está pronto. Em segundos você está dentro.
    </div>
    <style>{`
      @keyframes celebPop {
        0%   { transform: scale(0.4); opacity: 0; }
        60%  { transform: scale(1.12); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
    `}</style>
  </div>
);

// Clube Social: passo da PROPOSTA (objetivo + regras + data + convidar mútuos).
function StepProposta({ data, setData, T }: any) {
  const set = (patch: any) => setData((d: any) => ({ ...d, ...patch }));
  const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
  const monoLabel: React.CSSProperties = { fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-3)' };
  return (
    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div className="serif" style={{ fontSize: 24 }}>O convite</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>É uma proposta: você convida, a galera curte e ajuda a moldar, e você lança quando estiver bom.</div>
      </div>
      <div style={{ background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 18, padding: 15 }}>
        <div style={{ ...monoLabel, color: 'var(--ember-2)' }}>Objetivo · o coração do {T.s}</div>
        <textarea value={data.objective || ''} onChange={e => set({ objective: e.target.value })} rows={4}
          placeholder="Descreva o que você quer com este clube: o estilo de leitura, o estilo de debate, o objetivo — pra todo mundo entrar em sintonia."
          style={{ width: '100%', marginTop: 10, boxSizing: 'border-box', resize: 'vertical', background: 'transparent', border: 'none', outline: 'none', color: 'var(--ink)', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 16, lineHeight: 1.4 }} />
      </div>
      <div>
        <div style={monoLabel}>Regras (opcional)</div>
        <textarea value={data.rulesText || ''} onChange={e => set({ rulesText: e.target.value })} rows={3}
          placeholder="Ex: um marco por semana · sem spoilers além do marco · uma reflexão por marco"
          style={{ width: '100%', marginTop: 8, boxSizing: 'border-box', resize: 'vertical', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px', color: 'var(--ink)', fontSize: 13.5, lineHeight: 1.5 }} />
      </div>
      <div>
        <div style={monoLabel}>Data de início <span style={{ color: 'var(--ember)' }}>*</span></div>
        <div style={{ display: 'flex', gap: 9, marginTop: 8 }}>
          <input type="datetime-local" value={data.proposedStartAt || ''} onChange={e => set({ proposedStartAt: e.target.value })}
            style={{ flex: 1, minWidth: 0, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '13px 14px', color: 'var(--ink)', fontSize: 13.5, outline: 'none' }} />
          <button onClick={() => set({ proposedStartAt: nowLocal() })} style={{ background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 14, padding: '0 16px', color: 'var(--ember-2)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>HOJE</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 7 }}>"Hoje" começa o {T.s} imediatamente quando você lançar.</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: 13 }}>
        <span style={{ fontSize: 20 }}>📨</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, color: 'var(--ink)' }}>Convidar todos meus mútuos</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Quem você segue e te segue recebe o convite.</div>
        </div>
      </div>
    </div>
  );
}

export const ClubCreateFlow: React.FC<{
  term?: 'clube' | 'grupo';
  onBack: () => void;
  onCreated: (data: ClubFormData, clubId: string | null) => void;
  /** Quando definido, é chamado no último passo antes de mostrar celebração.
   *  Deve retornar { id } com o ID do clube criado. Se throw, mostra erro. */
  onSubmit?: (data: ClubFormData) => Promise<{ id: string } | { clubId: string } | void>;
  /** Faz upload de uma imagem de capa e retorna a URL servida pelo backend.
   *  O caller (App.tsx) injeta a session no wrapper. */
  onUploadCover?: (file: File) => Promise<string>;
  /** Biblioteca do usuário (livros que ele já tem) — pro seletor "Meus livros". */
  library?: any[];
  /** Clube Social: quando true, o fluxo vira PROPOSTA (objetivo/regras/data/convite). */
  propose?: boolean;
}> = ({ term = 'clube', onBack, onCreated, onSubmit, onUploadCover, library = [], propose = false }) => {
  const T = useTerm(term);
  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState<'form' | 'submitting' | 'celebrating' | 'error'>('form');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdClubId, setCreatedClubId] = useState<string | null>(null);
  const [data, setData] = useState<ClubFormData>({
    name: '',
    coverTemplate: 'embers',
    description: '',
    mode: 'group-read',
    privacy: 'closed',
    postBy: 'all',
    storiesPostBy: 'all',
    openWhen: 'always',
    openDays: [1, 4],
    allowPoke: true,
    publicRanking: true,
    allowMemberQuiz: false,
    // Defaults do cronograma — usuario pode mudar no step Cronograma.
    totalBooksPlanned: 1,
    durationCount: 1,
    durationUnit: 'meses',
    noDeadline: false,
    sequenceBooks: [],
    startEmpty: false,
    propose,
    objective: '',
    rulesText: '',
    proposedStartAt: null,
  });

  // Sequência dinâmica:
  // - group-read: identity → mode → schedule → sequence → privacy → rules → confirm (7)
  // - shared-habit: identity → mode → schedule → sequence → privacy → rules → confirm (7)
  //   (mesmos passos — diferença e backend: livros do shared-habit viram club_member_books
  //    do owner, ao inves de club_books compartilhado)
  // Fluxo enxuto: QUIZ (deriva modo/privacidade/cronograma/regras) → NOME →
  // LIVROS → PRONTO. Tudo o que o quiz define é editável depois nas Configurações.
  // Propor: LIDERA com o passo Proposta (objetivo/regras/data/convidar) pra ficar
  // claro de cara que é uma proposta — depois nome, detalhes e livros (reusando
  // todos os campos do criar normal). 'quiz' precisa vir antes de 'sequence'
  // (define totalBooksPlanned, que a sequência usa).
  const stepTypes = propose
    ? ['proposta', 'identity', 'quiz', 'sequence', 'confirm']
    : ['quiz', 'identity', 'sequence', 'confirm'];
  const total = stepTypes.length;
  const currentType = stepTypes[step - 1];
  const labelMap: Record<string, string> = {
    quiz: 'Sobre', identity: 'Nome', sequence: 'Livros', proposta: 'Proposta', confirm: 'Pronto',
    // legados (componentes ainda existem, mas fora do fluxo atual)
    mode: 'Modo', schedule: 'Cronograma', book: 'Livro', privacy: 'Privacidade', rules: 'Regras',
  };
  const labels = stepTypes.map(t => labelMap[t]);

  const canAdvance = (() => {
    switch (currentType) {
      case 'quiz':     return !!data.mode && !!data.privacy && !!data.postBy
                          && !!data.totalBooksPlanned && data.totalBooksPlanned > 0
                          && (data.noDeadline || (!!data.durationCount && data.durationCount > 0));
      case 'identity': return (data.name || '').trim().length > 0;
      case 'mode':     return !!data.mode;
      case 'schedule': return !!data.totalBooksPlanned && data.totalBooksPlanned > 0
                          && (data.noDeadline || (!!data.durationCount && data.durationCount > 0));
      case 'sequence': return data.startEmpty
                          || (data.sequenceBooks || []).some((b: any) => b && b.title && b.author && b.totalPages);
      case 'proposta': return !!data.proposedStartAt && (data.objective || '').trim().length > 0;
      case 'book':     return !!data.book?.title?.trim() && !!data.book?.author?.trim() && !!data.book?.totalPages;
      case 'privacy':  return !!data.privacy;
      case 'rules':    return !!data.postBy && !!data.openWhen;
      case 'confirm':  return true;
      default:         return false;
    }
  })();

  const advance = async () => {
    if (step < total) {
      setStep(step + 1);
      return;
    }
    // Step 5 — submit
    if (onSubmit) {
      setPhase('submitting');
      setSubmitError(null);
      try {
        const result: any = await onSubmit(data);
        const id = result?.id || result?.clubId || null;
        setCreatedClubId(id);
        setPhase('celebrating');
        setTimeout(() => onCreated(data, id), 1500);
      } catch (e: any) {
        // Traduz erros técnicos pra mensagens amigáveis
        const msg = e?.message || '';
        let friendly = 'Erro ao criar clube';
        if (msg === 'mentor_required') {
          friendly = e?.dados?.message || 'Criar clubes é para Mentores do Forjamente. Você pode entrar e participar de qualquer clube à vontade.';
        } else if (msg.includes('plan_limit_reached') || msg.includes('plan_required')) {
          if (data.privacy === 'open') {
            friendly = 'Você atingiu o limite de clubes públicos do seu plano. Faça upgrade pra criar mais.';
          } else {
            friendly = 'Você atingiu o limite de clubes fechados do seu plano. Faça upgrade pra criar mais.';
          }
        } else if (msg.includes('Nome inválido')) {
          friendly = 'Nome inválido — use entre 1 e 80 caracteres.';
        } else if (msg.includes('mode inválido')) {
          friendly = 'Tipo de leitura inválido. Volte e selecione novamente.';
        } else if (msg) {
          friendly = msg;
        }
        setSubmitError(friendly);
        setPhase('error');
      }
    } else {
      setPhase('celebrating');
      setTimeout(() => onCreated(data, null), 1900);
    }
  };
  const retreat = () => {
    if (step > 1) setStep(step - 1);
    else onBack();
  };

  if (phase === 'celebrating') {
    return (
      <div className="scroll" style={{ paddingBottom: 0, height: '100%' }}>
        <CelebrationScreen data={data} T={T}/>
      </div>
    );
  }

  if (phase === 'submitting') {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '40px 24px',
        background: 'var(--paper)',
      }}>
        <div className="serif" style={{ fontSize: 28, color: 'var(--ink-2)', fontStyle: 'italic' }}>
          Criando seu {T.s}…
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="scroll" style={{ padding: '60px 20px' }}>
        <div className="serif" style={{ fontSize: 28, lineHeight: 1.05, marginBottom: 12 }}>
          Não consegui criar.
        </div>
        <div style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 24 }}>
          {submitError}
        </div>
        <button onClick={() => { setPhase('form'); setSubmitError(null); }} style={{
          width: '100%', padding: '14px 18px', borderRadius: 16,
          background: 'var(--ink)', color: 'var(--paper)',
          fontSize: 14, fontWeight: 500,
        }}>
          Tentar de novo
        </button>
        <button onClick={onBack} style={{
          width: '100%', padding: '14px', marginTop: 8,
          background: 'transparent', color: 'var(--ink-3)', fontSize: 13,
        }}>Voltar</button>
      </div>
    );
  }

  return (
    <div className="scroll" style={{ paddingBottom: 130 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={retreat} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
        }}>
          <Icon name={step === 1 ? 'close' : 'chevron-left'} size={16}/>
        </button>
      </div>
      <div style={{ padding: '20px 20px 0' }}>
        <StepperBar step={step} total={total} labels={labels}/>
      </div>
      <div style={{ marginTop: 18, animation: 'fadeIn 220ms ease-out' }} key={`${currentType}-${step}`}>
        {currentType === 'quiz'     && <StepQuiz      data={data} setData={setData} T={T}/>}
        {currentType === 'identity' && <Step1Identity data={data} setData={setData} T={T} onUploadCover={onUploadCover}/>}
        {currentType === 'mode'     && <Step2Mode     data={data} setData={setData} T={T}/>}
        {currentType === 'schedule' && <StepSchedule  data={data} setData={setData} T={T}/>}
        {currentType === 'sequence' && <StepSequence  data={data} setData={setData} T={T} library={library}/>}
        {currentType === 'proposta' && <StepProposta  data={data} setData={setData} T={T}/>}
        {currentType === 'book'     && <Step3Book     data={data} setData={setData} T={T}/>}
        {currentType === 'privacy'  && <Step3Privacy  data={data} setData={setData} T={T}/>}
        {currentType === 'rules'    && <Step4Rules    data={data} setData={setData} T={T}/>}
        {currentType === 'confirm'  && <Step5Confirm  data={data} T={T}/>}
      </div>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: '12px 20px 26px',
        background: 'linear-gradient(to top, var(--paper) 60%, rgba(0,0,0,0))',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {/* O que falta para seguir (pente fino, UX-30): o botão apagado não
            dizia nada, e no passo dos livros a pessoa ficava presa. */}
        {!canAdvance && (
          <div role="status" style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--ink-3)' }}>
            {currentType === 'sequence' ? 'Escolha um livro para continuar — ou toque em "Definir depois".'
              : currentType === 'identity' ? `Dê um nome ao ${T.s} para continuar.`
              : currentType === 'quiz' ? 'Responda as perguntas acima para continuar.'
              : currentType === 'proposta' ? 'Preencha o objetivo e a data de início para continuar.'
              : 'Complete este passo para continuar.'}
          </div>
        )}
        <button onClick={canAdvance ? advance : undefined} disabled={!canAdvance} style={{
          width: '100%', padding: '15px',
          borderRadius: 16,
          background: canAdvance
            ? (step === total ? 'var(--ember)' : 'var(--ink)')
            : 'var(--paper-3)',
          color: canAdvance ? 'var(--paper)' : 'var(--ink-4)',
          fontSize: 15, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          cursor: canAdvance ? 'pointer' : 'not-allowed',
          boxShadow: canAdvance && step === total ? '0 14px 28px -10px rgba(199,83,26,0.55)' : 'none',
        }}>
          {step === total ? (propose ? 'Enviar convites' : 'Criar ' + T.s) : 'Continuar'}
          {canAdvance && <Icon name={step === total ? 'check' : 'arrow-right'} size={15} color="var(--paper)" stroke={2.4}/>}
        </button>
      </div>
    </div>
  );
};

export const ClubEmptyScreen: React.FC<{
  data?: Partial<ClubFormData>;
  term?: 'clube' | 'grupo';
  clubId?: string | null;
  onInvite?: () => void;
  onSetMilestone?: () => void;
  onBack?: () => void;
  onEnterClub?: () => void;
}> = ({ data = {}, term = 'clube', clubId, onInvite, onSetMilestone, onBack, onEnterClub }) => {
  const T = useTerm(term);
  const tplObj = COVER_TEMPLATES.find(t => t.id === data.coverTemplate) || COVER_TEMPLATES[0];
  const name = data.name || 'Seu ' + T.s;
  return (
    <div className="scroll" style={{ paddingBottom: 130 }}>
      <div style={{ position: 'relative' }}>
        <div style={{ height: 200, background: tplObj.bg, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -30, opacity: 0.32 }}>
            {tplObj.motif === 'flame' && <svg width="220" height="220" viewBox="0 0 40 60" fill={tplObj.accent}><path d="M20 56C10 50 6 38 12 26c2 4 2 8 5 10 1-6-3-12 0-18 6 6 12 14 12 22 0 12-9 22-17 22z"/></svg>}
            {tplObj.motif === 'leaf' && <svg width="220" height="220" viewBox="0 0 60 60" fill={tplObj.accent}><path d="M50 8C24 12 8 28 6 50c14-12 30-22 38-32 2-4-1-15-1-14z"/></svg>}
            {tplObj.motif === 'spark' && <svg width="220" height="220" viewBox="0 0 60 60" fill={tplObj.accent}><path d="M30 4l4 18 18 4-18 4-4 18-4-18-18-4 18-4z"/></svg>}
          </div>
          <button onClick={onBack} style={{
            position: 'absolute', top: 54, left: 16,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(10,8,6,0.4)', backdropFilter: 'blur(8px)',
            color: '#F3ECDE',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <Icon name="chevron-left" size={16} color="#F3ECDE"/>
          </button>
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 18, color: '#F3ECDE' }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(243,236,222,0.7)',
            }}>recém criado · você é líder</div>
            <div className="serif" style={{ fontSize: 30, lineHeight: 1.05, marginTop: 4 }}>{name}</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '32px 20px 0' }}>
        <div className="serif" style={{
          fontSize: 28, lineHeight: 1.1, letterSpacing: '-0.01em', maxWidth: 320,
        }}>
          Seu {T.s} está pronto.<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>Falta o mais importante</span>: leitores.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 12, lineHeight: 1.5, maxWidth: 320 }}>
          Convide as primeiras pessoas. Quanto antes elas chegarem, mais cedo o {T.s} ganha vida.
        </div>
      </div>

      {/* CTA principal: Entrar no grupo agora (se já temos o ID do clube criado).
          Deixa o usuário sair do empty state pra dentro do grupo recém criado. */}
      {clubId && onEnterClub && (
        <div style={{ padding: '24px 20px 0' }}>
          <button onClick={onEnterClub} style={{
            width: '100%', padding: '16px 18px', borderRadius: 18,
            background: 'var(--ember)', color: 'var(--paper)',
            display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: '0 18px 36px -14px rgba(199,83,26,0.55)',
            textAlign: 'left',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon name="arrow-right" size={20} color="var(--paper)"/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 500 }}>Entrar no {T.s} agora</div>
              <div className="mono" style={{
                fontSize: 9.5, letterSpacing: '0.10em', opacity: 0.85,
                marginTop: 2, textTransform: 'uppercase',
              }}>ver feed · ranking · estudo</div>
            </div>
            <Icon name="chevron-right" size={16} color="var(--paper)"/>
          </button>
        </div>
      )}

      <div style={{ padding: '12px 20px 0' }}>
        <button onClick={onInvite} style={{
          width: '100%', padding: '16px 18px',
          borderRadius: 18,
          background: clubId ? 'var(--ink)' : 'var(--ember)',
          color: 'var(--paper)',
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: clubId ? '0 12px 28px -16px rgba(0,0,0,0.4)' : '0 18px 36px -14px rgba(199,83,26,0.55)',
          textAlign: 'left',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name="share" size={20} color="var(--paper)"/>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 500 }}>Convidar primeiros membros</div>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.10em', opacity: 0.85,
              marginTop: 2, textTransform: 'uppercase',
            }}>link · QR · stories</div>
          </div>
          <Icon name="arrow-right" size={16} color="var(--paper)"/>
        </button>
      </div>

      {onSetMilestone && (
        <div style={{ padding: '12px 20px 0' }}>
          <button onClick={onSetMilestone} style={{
            width: '100%', padding: '14px 16px',
            borderRadius: 16,
            background: 'var(--paper-2)',
            border: '1px solid var(--line-strong)',
            display: 'flex', alignItems: 'center', gap: 12,
            textAlign: 'left',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--paper-3)', color: 'var(--ink-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 18 }}>✦</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>Definir o primeiro marco</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
                Estruture os marcos antes mesmo dos membros chegarem.
              </div>
            </div>
            <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
          </button>
        </div>
      )}

      <div style={{ padding: '32px 20px 0' }}>
        <div style={{
          padding: '14px 16px', borderRadius: 14,
          background: 'var(--paper-2)',
          border: '1px dashed var(--line-strong)',
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 18, lineHeight: 1, marginTop: 2 }}>📖</div>
          <div style={{ flex: 1, fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.55 }}>
            Não precisa publicar nada agora. Quando os primeiros membros chegarem, você vai ver tudo se mover.
          </div>
        </div>
      </div>
    </div>
  );
};
